import { createContext, useContext, useMemo } from 'react';

import type { OldRouterNewContextValue } from 'js/lib/useRouter';
import { OldRouterNewContext } from 'js/lib/useRouter';

import { isEmbeddedExperience } from './lib/embeddedExperienceUtil';
import { resolveEmbeddedLaunchContext } from './lib/embeddedLaunchContext';
import type { EmbedScope } from './lib/embeddedLaunchContext';

// Tests commonly mock js/lib/useRouter without exporting the context; fall back to an empty
// context so this shared hook doesn't crash those renders.
const RouterContext = OldRouterNewContext ?? createContext<OldRouterNewContextValue>({});

export type EmbeddedExperience = {
  /** Whether the simplified in-frame experience is active for this launch. */
  isEmbeddedView: boolean;
  /** Launch granularity: full course, a single module, or a single item/video; undefined if not an embed. */
  embedScope?: EmbedScope;
  moduleId?: string;
  itemId?: string;
  /** Embedded with a single launched module — show only that module in the outline. */
  isModuleEmbedView: boolean;
  /** Embedded with a single item / assignment / video — no outline. */
  isSingleItemEmbedView: boolean;
  /** SSR-safe launch hint (`?isExternal=true`) — usable before client-side embed detection runs. */
  isEmbeddedLaunchUrl: boolean;
  /** Org-level gate for hiding the Help button, resolved at LTI login. */
  isHelpButtonDisabledForOrg: boolean;
};

/**
 * The single source of embedded-experience state for in-frame learner surfaces. Active when
 * the launch is an LMS embed, inside an iframe, with partitioned cookies usable, for an org
 * that has it enabled. Consumers read the boolean they need (e.g. `isSingleItemEmbedView`).
 */
export const useEmbeddedExperience = (): EmbeddedExperience => {
  // Read the context directly (not useLocation, which throws without a router provider).
  const { location } = useContext(RouterContext) ?? {};
  const isEmbeddedLaunchUrl = location?.query?.isExternal === 'true';

  return useMemo(() => {
    const { embedScope, moduleId, itemId, isHelpButtonDisabledForOrg } = resolveEmbeddedLaunchContext();
    const isEmbeddedView = isEmbeddedExperience();
    return {
      isEmbeddedView,
      embedScope,
      moduleId,
      itemId,
      isModuleEmbedView: isEmbeddedView && embedScope === 'module',
      isSingleItemEmbedView: isEmbeddedView && embedScope === 'item',
      isEmbeddedLaunchUrl,
      isHelpButtonDisabledForOrg: isHelpButtonDisabledForOrg ?? false,
    };
  }, [isEmbeddedLaunchUrl]);
};
